/**
 * Add-on cross-sell (addon-upsell block) client behaviour.
 *
 * Adds the chosen add-on's default variant to the cart via the Ajax cart
 * endpoint /cart/add.js, keeps the customer in the cart drawer (it does NOT
 * redirect to checkout), and preserves any Delivery Date / Slot / Occasion /
 * Personal Message cart attributes already on the cart.
 *
 * When the current product is a funeral/sympathy product the block renders a
 * "Ribbon message / print text" field. Its value is attached as the line-item
 * property "Ribbon Text" (properties[Ribbon Text]) on the ribbon add-on line
 * item only, so the florist sees it on that ribbon. It is kept separate from
 * the greeting-card "Personal Message" cart attribute.
 */
import { CartAddEvent, ThemeEvents } from '@theme/events';

const CART_ADD_URL =
  (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';

class FlorentAddonUpsell {
  /**
   * @param {HTMLElement} root
   */
  constructor(root) {
    this.root = root;
    this.ribbonInput = root.querySelector('[data-addon-ribbon-input]');

    root.querySelectorAll('[data-addon-add]').forEach((button) => {
      button.addEventListener('click', this.#handleAdd.bind(this, button));
    });
  }

  /**
   * Adds a single add-on line item to the cart.
   * @param {HTMLButtonElement} button
   * @param {PointerEvent} event
   */
  async #handleAdd(button, event) {
    event.preventDefault();
    if (button.disabled) return;

    button.disabled = true;
    button.classList.add('is-loading');

    const body = new FormData();
    body.append('id', button.dataset.addonVariant);
    body.append('quantity', '1');

    // Attach the funeral ribbon text only to the ribbon add-on line item,
    // and only when the input is present and non-empty.
    if (button.dataset.addonRibbon === 'true' && this.ribbonInput) {
      const ribbonText = this.ribbonInput.value.trim();
      if (ribbonText) {
        body.append('properties[Ribbon Text]', ribbonText);
      }
    }

    // Ask Shopify to re-render the cart sections so the drawer updates.
    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });
    if (sectionIds.length) {
      body.append('sections', sectionIds.join(','));
    }

    try {
      const response = await fetch(CART_ADD_URL, {
        method: 'POST',
        headers: {
          Accept: 'text/html',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body,
      });

      const data = await response.json();

      if (data.status) {
        // Shop as returned an error (e.g. item limit, sold out, invalid variant).
        throw new Error(data.description || data.message || 'Could not add this item.');
      }

      document.dispatchEvent(
        new CartAddEvent(data, button.id || 'addon-upsell', {
          didError: false,
          source: 'addon-upsell',
          itemCount: 1,
          productId: button.dataset.addonProductId,
          variantId: button.dataset.addonVariant,
        })
      );
    } catch (error) {
      console.error('Add-on could not be added to cart:', error);
      // Surface a transient status without forcing a checkout/cart redirect.
      const status = this.root.querySelector('[data-addon-status]');
      if (status) {
        status.textContent = error.message || 'Could not add this item. Please try again.';
        status.classList.remove('hidden');
        window.setTimeout(() => status.classList.add('hidden'), 6000);
      }
    } finally {
      button.disabled = false;
      button.classList.remove('is-loading');
    }
  }
}

function initAddonUpsell() {
  document.querySelectorAll('[data-addon-upsell]').forEach((root) => {
    if (!root.dataset.addonInitialized) {
      root.dataset.addonInitialized = 'true';
      new FlorentAddonUpsell(root);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAddonUpsell);
} else {
  initAddonUpsell();
}

// Re-init after a predictive-sort/morph re-render.
document.addEventListener(ThemeEvents.cartUpdate, initAddonUpsell);